import { chipToolchains, defaultToolchain, type TargetTable, type ToolchainName } from "idfweb-assets";
import type { Task } from "idfweb-tasks";
import { exists, mkdirp, readFile, rmTree, type Vfs } from "idfweb-vfs";

import { lockPathHook } from "./lock-path.ts";
import type { ConfigurePreset } from "./presets.ts";
import { watchProgress, type WatchOptions } from "./progress.ts";
import { GUEST_IDF, GUEST_USR, TOOLCHAIN_FILE, runInToolchain, type Toolchain } from "./toolchain.ts";

/** Build a project by generating a Ninja manifest with CMake and executing it. */
export interface BuildOptions {
    /**
     * The project directory, the opened tree or a directory in it, as
     * `idf.py -C` takes it. The build directory is its `build`, or the
     * preset's binaryDir.
     */
    project: string;
    /**
     * The configure preset to build with, as `idf.py --preset` does. CMake is
     * given its name; its IDF_TARGET is not read here, so `target` must match.
     */
    preset?: ConfigurePreset | undefined;
    /** Target chip name for ESP-IDF builds. */
    target?: string | undefined;
    /**
     * The ESP-IDF toolchain: clang and lld, or GCC and GNU ld. Without one,
     * the target's toolchain in `share/idfweb/targets.json`.
     */
    toolchain?: ToolchainName | undefined;
    /**
     * Build as the first build after `idf.py set-target`: the build directory is
     * emptied and ESP-IDF renames the existing sdkconfig to sdkconfig.old.
     */
    setTarget?: boolean | undefined;
    /** Force CMake reconfiguration even if build.ninja exists. */
    reconfigure?: boolean | undefined;
    /** Maximum concurrent Ninja jobs. */
    jobs: number;
    /**
     * Called once the build directory is configured, by this build or an
     * earlier one, before ninja runs. Ninja can still run CMake again for a
     * changed CMakeLists.txt or sdkconfig.
     */
    onConfigured?: (() => void) | undefined;
    /** Task for progress reporting across configure and build steps. */
    task?: Task | undefined;
    onStdout?: ((bytes: Uint8Array) => void) | undefined;
    onStderr?: ((bytes: Uint8Array) => void) | undefined;
}

export interface BuildStep {
    argv: string[];
    exitCode: number;
    stdout: string;
    stderr: string;
}

export interface BuildResult {
    steps: BuildStep[];
    /** Guest path of the linked ELF, if produced. */
    elf: string | undefined;
}

export async function buildProject(tc: Toolchain, options: BuildOptions): Promise<BuildResult> {
    const opts = withToolchain(tc.vfs, options);
    const buildDir = buildDirectory(opts.project, opts.preset);
    const steps: BuildStep[] = [];

    const pending = pendingConfigure(tc.vfs, opts);
    if (pending !== undefined) {
        const configure = await configureProject(tc, pending);
        steps.push(configure);
        if (configure.exitCode !== 0) return { steps, elf: undefined };
    }

    opts.onConfigured?.();

    const ninja = [`${GUEST_USR}/bin/ninja`, `-j${opts.jobs}`];
    // Checked before every ninja run: menuconfig changes the sdkconfig without
    // a configure step here.
    if (opts.toolchain === "gcc" && sdkconfigValue(tc.vfs, buildDir, "COMPILER_LTO_LINKTIME") === true) {
        const stderr =
            "error: CONFIG_COMPILER_LTO_LINKTIME is set, and idfweb's GCC is built without" +
            " link-time optimization. Turn the option off in menuconfig.\n";
        opts.onStderr?.(new TextEncoder().encode(stderr));
        steps.push({ argv: ninja, exitCode: 1, stdout: "", stderr });
        return { steps, elf: undefined };
    }
    const build = await run(tc, opts, ninja, buildDir, { count: true });
    steps.push(build);

    return { steps, elf: build.exitCode === 0 ? findElf(tc.vfs, buildDir) : undefined };
}

/**
 * The options a build would configure the build directory with, or undefined
 * when it would go straight to ninja. A build directory configured for another
 * target or toolchain is configured as after `idf.py set-target`: CMake keeps
 * the compilers in its cache, and the sdkconfig keeps the old chip and the
 * choices whose defaults depend on the toolchain, such as the C library.
 */
export function pendingConfigure(vfs: Vfs, options: BuildOptions): BuildOptions | undefined {
    let opts = withToolchain(vfs, options);
    const target = opts.target ?? DEFAULT_TARGET;
    const configuredFor = configuredTarget(vfs, opts.project, opts.preset);
    const configuredWith = configuredToolchain(vfs, opts.project, opts.preset);
    const was =
        configuredFor !== undefined && configuredFor !== target
            ? `for ${configuredFor}; building for ${target}`
            : configuredWith !== undefined && configuredWith !== opts.toolchain
              ? `with ${configuredWith}; building with ${opts.toolchain}`
              : undefined;
    if (!opts.setTarget && was !== undefined) {
        opts = { ...opts, setTarget: true };
        const text = `the build directory was configured ${was} empties it and moves sdkconfig to sdkconfig.old\n`;
        opts.onStdout?.(new TextEncoder().encode(text));
    }
    const buildDir = buildDirectory(opts.project, opts.preset);
    if (opts.setTarget || opts.reconfigure || !exists(vfs, `${buildDir}/build.ninja`)) return opts;
    return undefined;
}

/** Run the CMake step of a build and nothing after it, whatever the build directory holds. */
export async function configureProject(tc: Toolchain, options: BuildOptions): Promise<BuildStep> {
    const opts = withToolchain(tc.vfs, options);
    const project = opts.project;
    const buildDir = buildDirectory(project, opts.preset);
    if (opts.setTarget) cleanBuildTree(tc.vfs, project, opts.preset);
    mkdirp(tc.vfs, buildDir);
    const idf = isIdfProject(tc.vfs, project);
    const configure = await run(
        tc,
        opts,
        configureArgv(tc.vfs, opts, project, idf),
        idf ? project : undefined,
        { step: "Configuring" },
        // What `idf.py set-target` sets; ESP-IDF's project.cmake then renames
        // the sdkconfig, wherever the project put it.
        idf && opts.setTarget ? { _IDF_PY_SET_TARGET_ACTION: "1" } : undefined,
    );
    if (configure.exitCode === 0 && idf) warnOnReplacedDefaults(tc.vfs, buildDir, configure, opts);
    return configure;
}

/** The options with the toolchain filled in from the target when they set none. */
type ToolchainOptions = BuildOptions & { toolchain: ToolchainName };

function withToolchain(vfs: Vfs, opts: BuildOptions): ToolchainOptions {
    return { ...opts, toolchain: opts.toolchain ?? targetToolchain(vfs, opts.target ?? DEFAULT_TARGET) };
}

/**
 * The toolchain a chip builds with unless a build sets another, from
 * `share/idfweb/targets.json`, which the asset index repeats for the page.
 */
export function targetToolchain(vfs: Vfs, target: string): ToolchainName {
    let toolchains: Record<string, ToolchainName> = {};
    try {
        toolchains = chipToolchains(JSON.parse(new TextDecoder().decode(readFile(vfs, TARGETS_TABLE))) as TargetTable);
    } catch {
        // No table: clang for every chip.
    }
    return defaultToolchain(toolchains, target);
}

/** The build directory of a project directory, where `idf.py` puts it. */
export function buildDirectory(project: string, preset?: ConfigurePreset): string {
    return preset?.binaryDir ?? `${project}/build`;
}

/** Construct CMake command-line arguments to generate the Ninja build manifest. */
function configureArgv(vfs: Vfs, opts: BuildOptions, project: string, idf: boolean): string[] {
    const argv = [
        `${GUEST_USR}/bin/cmake`,
        "-S",
        project,
        "-B",
        buildDirectory(project, opts.preset),
        "-G",
        "Ninja",
        `-DCMAKE_MAKE_PROGRAM=${GUEST_USR}/bin/ninja`,
    ];
    if (opts.preset !== undefined) argv.push("--preset", opts.preset.name);

    if (!idf) {
        argv.push(`-DCMAKE_TOOLCHAIN_FILE=${TOOLCHAIN_FILE}`);
        return argv;
    }

    const target = opts.target ?? DEFAULT_TARGET;
    const gcc = opts.toolchain === "gcc";
    argv.push(
        `-DCMAKE_TOOLCHAIN_FILE=${GUEST_IDF}/tools/cmake/toolchain-${gcc ? "" : "clang-"}${target}.cmake`,
        `-DPYTHON=${GUEST_USR}/bin/python3`,
        `-DIDF_TARGET=${target}`,
        `-DIDF_TOOLCHAIN=${gcc ? "gcc" : "clang"}`,
        `-DSDKCONFIG_DEFAULTS=${sdkconfigDefaults(vfs, project, opts.preset, gcc).join(";")}`,
        "-DCONFIGDEP_ENABLE=1",
        lockPathHook(vfs, buildDirectory(project, opts.preset)),
    );
    return argv;
}

/**
 * Return sdkconfig defaults files: project defaults, and LLD defaults for
 * clang. The project's are the preset's SDKCONFIG_DEFAULTS if it sets any,
 * since the command line's would replace them; ESP-IDF resolves relative ones
 * against the project directory. An empty list still replaces a cached one.
 */
function sdkconfigDefaults(vfs: Vfs, project: string, preset: ConfigurePreset | undefined, gcc: boolean): string[] {
    const defaults = `${project}/sdkconfig.defaults`;
    const files: string[] = [];
    const fromPreset = preset?.cacheVariables.SDKCONFIG_DEFAULTS;
    if (fromPreset !== undefined) files.push(...fromPreset.split(";").filter(Boolean));
    else if (exists(vfs, defaults)) files.push(defaults);
    if (!gcc) files.push(LLD_DEFAULTS);
    return files;
}

/**
 * Warn when the configure did not use the defaults files passed to it, which
 * happens when a project's CMakeLists.txt sets SDKCONFIG_DEFAULTS instead of
 * appending to it. The list is read from `project_description.json`.
 */
function warnOnReplacedDefaults(vfs: Vfs, buildDir: string, configure: BuildStep, opts: BuildOptions): void {
    const used = configDefaults(vfs, buildDir);
    if (used === undefined) return;
    if (opts.toolchain === "gcc" || used.includes(LLD_DEFAULTS)) return;
    const text =
        `warning: the configure did not use ${LLD_DEFAULTS}, which sets the options` +
        " an idfweb build needs. The project's CMakeLists.txt probably sets" +
        " SDKCONFIG_DEFAULTS; use list(APPEND SDKCONFIG_DEFAULTS ...) to keep the files" +
        " passed on the command line.\n";
    configure.stderr += text;
    opts.onStderr?.(new TextEncoder().encode(text));
}

/** The defaults files the last configure used, or undefined if it wrote no description. */
function configDefaults(vfs: Vfs, buildDir: string): string[] | undefined {
    try {
        const description = readFile(vfs, `${buildDir}/project_description.json`);
        const json = new TextDecoder().decode(description);
        const value: unknown = (JSON.parse(json) as { config_defaults?: unknown }).config_defaults;
        return typeof value === "string" ? value.split(";") : undefined;
    } catch {
        return undefined;
    }
}

/** An option's value in the build directory's `config/sdkconfig.json`, or undefined. */
function sdkconfigValue(vfs: Vfs, buildDir: string, name: string): unknown {
    try {
        const json = new TextDecoder().decode(readFile(vfs, `${buildDir}/config/sdkconfig.json`));
        return (JSON.parse(json) as Record<string, unknown>)[name];
    } catch {
        return undefined;
    }
}

/** Check whether a project CMakeLists.txt references IDF_PATH. */
export function isIdfProject(vfs: Vfs, project: string): boolean {
    try {
        const cmakeLists = readFile(vfs, `${project}/CMakeLists.txt`);
        return new TextDecoder().decode(cmakeLists).includes("IDF_PATH");
    } catch {
        return false;
    }
}

export const DEFAULT_TARGET = "esp32c3";

/** Empty a project's build directory, creating it if it is missing. */
export function cleanBuildTree(vfs: Vfs, project: string, preset?: ConfigurePreset): void {
    const buildDir = buildDirectory(project, preset);
    mkdirp(vfs, buildDir);
    for (const entry of vfs.readdir(buildDir)) rmTree(vfs, `${buildDir}/${entry.name}`);
}

/**
 * The chip target a project's build directory was configured for, from
 * `IDF_TARGET` in its CMakeCache.txt, or undefined when it has none.
 */
export function configuredTarget(vfs: Vfs, project: string, preset?: ConfigurePreset): string | undefined {
    return cacheEntry(vfs, project, preset, "IDF_TARGET");
}

/** The toolchain a project's build directory was configured with, from `IDF_TOOLCHAIN` in its CMakeCache.txt. */
function configuredToolchain(vfs: Vfs, project: string, preset?: ConfigurePreset): string | undefined {
    return cacheEntry(vfs, project, preset, "IDF_TOOLCHAIN");
}

/** A variable's value in a build directory's CMakeCache.txt, or undefined when it has none. */
function cacheEntry(vfs: Vfs, project: string, preset: ConfigurePreset | undefined, name: string): string | undefined {
    try {
        const cache = new TextDecoder().decode(readFile(vfs, `${buildDirectory(project, preset)}/CMakeCache.txt`));
        return new RegExp(`^${name}:\\w+=(.+)$`, "m").exec(cache)?.[1]?.trim();
    } catch {
        return undefined;
    }
}

const LLD_DEFAULTS = `${GUEST_USR}/share/idfweb/lld.defaults`;
const TARGETS_TABLE = `${GUEST_USR}/share/idfweb/targets.json`;

/** Execute a single build step and track progress. */
async function run(
    tc: Toolchain,
    opts: ToolchainOptions,
    argv: string[],
    cwd: string | undefined,
    watching: WatchOptions,
    env?: Record<string, string> | undefined,
): Promise<BuildStep> {
    const task = opts.task;
    const watch = task === undefined ? undefined : watchProgress(task, watching);
    const onStdout =
        watch === undefined
            ? opts.onStdout
            : (bytes: Uint8Array) => {
                  watch(bytes);
                  opts.onStdout?.(bytes);
              };
    const result = await runInToolchain(tc, argv, {
        cwd,
        env: { IDF_TOOLCHAIN: opts.toolchain, ...env },
        onStdout,
        onStderr: opts.onStderr,
    });
    return { argv, ...result };
}

/** Find the first generated ELF executable in the root of the build directory. */
function findElf(vfs: Vfs, buildDir: string): string | undefined {
    const elf = vfs.readdir(buildDir).find((entry) => entry.name.endsWith(".elf"));
    return elf === undefined ? undefined : `${buildDir}/${elf.name}`;
}
